import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { ExpertIdPill } from "../../../../components/ExpertIdPill/ExpertIdPill";
import { IconUser } from "../../../../utils/icons";
import {
  convertInsta,
  convertLinkedIn,
  convertPinterest,
  convertTikTok,
  convertTwitter,
  convertWebsite,
  getExpertProfessionList,
  getTextOverflowProps,
  listViewIcons,
  listViewStatus,
} from "./helper";
import { CircularPercentage } from "../../createEditProfile/formFragments/CircularPercentage";
import { getFullProfilePercentage } from "../../createEditProfile/tabs";
import { cldUrl } from "../../../../utils/cloudinary";
import { ISinglePro } from "../../../../components/singlePro/state";

interface ExpertCardContentProps {
  pro: ISinglePro;
  onNameClick?: () => void;
  onAvatarClick?: () => void;
  showEngagement?: boolean;
  showSocials?: boolean;
  showStatus?: boolean;
  showId?: boolean;
  copyableId?: boolean;
}

export function ExpertCardContent({
  pro,
  onNameClick,
  onAvatarClick,
  showEngagement = true,
  showSocials = true,
  showStatus = true,
  showId = true,
}: ExpertCardContentProps) {
  const statusColor = useMemo(
    () =>
      listViewStatus[pro.status as keyof typeof listViewStatus] || "#AB854A",
    [pro.status]
  );

  const isValidUrl = (url?: string) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.length < 3) return false;
      return true;
    } catch {
      return false;
    }
  };

  const getSocialIcon = (property: keyof ISinglePro, url?: string | null) => {
    if (!url || !isValidUrl(url) || !pro[property]) return null;
    return (
      <Link to={url} target="_blank" onClick={(e) => e.stopPropagation()}>
        {/* text-accent feeds currentColor to the social icons, whose color
            arrives as an SVG attribute that cannot resolve var(). */}
        <div className="p-[6px] rounded-full bg-tint text-accent flex">
          {listViewIcons[property as keyof typeof listViewIcons]}
        </div>
      </Link>
    );
  };

  return (
    <div className="flex gap-3 sm:gap-4 flex-row w-full min-w-0">
      {pro.avatar ? (
        <img
          className="w-[72px] h-[72px] sm:w-[114px] sm:h-[114px] max-h-[114px] !shrink-0 rounded-2xl object-cover object-top"
          src={cldUrl(pro.avatar, {
            w: 200,
            h: 200,
            crop: "fill",
            gravity: "face",
          })}
          alt={pro.displayName}
          onClick={onAvatarClick}
        />
      ) : (
        // Placeholder wash stays literal: bg-opacity-* cannot tint a
        // var()-backed color, so bg-tint would drop the 20% translucency.
        <div className="w-[72px] h-[72px] sm:w-[114px] sm:h-[114px] flex items-center !shrink-0 justify-center rounded-2xl !bg-[rgba(242,235,226,0.2)] dark:!bg-[rgba(183,156,115,0.16)] !border-0">
          <IconUser width={32} height={32} />
        </div>
      )}

      <div className="w-full min-w-0 flex flex-col gap-2">
        <div className="flex gap-2 sm:gap-4 flex-col sm:flex-row sm:items-center pr-8 sm:pr-0">
          <Typography
            onClick={onNameClick}
            variant="h5"
            fontFamily="CircularStdBlack"
            className={classNames(
              "text-base sm:text-lg text-text-1 font-bold text-ellipsis overflow-hidden whitespace-nowrap",
              { "cursor-pointer hover:text-accent-text": !!onNameClick }
            )}
          >
            {pro.displayName}
          </Typography>
          <div className="flex gap-2 items-center flex-wrap">
            {showEngagement && (
              <div className="flex items-center justify-center !py-1">
                <CircularPercentage
                  value={getFullProfilePercentage(pro)}
                  size={24}
                />
              </div>
            )}
            {showSocials && (
              <div className="flex items-center flex-wrap socials gap-2">
                {getSocialIcon("twitter", convertWebsite(pro.website))}
                {getSocialIcon("website", convertTwitter(pro.twitter))}
                {getSocialIcon("instagram", convertInsta(pro.instagram))}
                {getSocialIcon("tiktok", convertTikTok(pro.tiktok))}
                {getSocialIcon("pinterest", convertPinterest(pro.pinterest))}
                {getSocialIcon("linkedin", convertLinkedIn(pro.linkedin))}
                {getSocialIcon("facebook", pro.facebook)}
              </div>
            )}
            {showStatus && (
              <Typography
                variant="subtitle2"
                className="!text-[9px] uppercase rounded-3xl px-3 py-2 border !font-bold flex items-center h-[23px]"
                style={{ borderColor: statusColor, color: statusColor }}
              >
                {pro.status === "" ? "N/A" : pro.status}
              </Typography>
            )}
            {showId && <ExpertIdPill userid={pro.userid} />}
          </div>
        </div>

        <div className="flex gap-2 flex-col items-start sm:items-center w-full sm:w-[90%]">
          {getExpertProfessionList(pro)}

          <div className="flex flex-col gap-1 w-full text-text-2">
            {pro?.description?.trim() ? (
              <div style={getTextOverflowProps(2)} className="text-text-1 text-sm sm:text-base">
                {pro.description}
              </div>
            ) : (
              // `gray-400` does not exist in this Tailwind config, so this
              // inherits the parent's --text-2 today; text-muted would newly
              // lighten it. Left as-is.
              <div className="mb-2 text-gray-400">N/A</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
